import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, Form, Modal, Alert, Placeholder, ButtonGroup } from 'react-bootstrap';
import {
    fetchDirectiva,
    createDirigente,
    updateDirigente,
    deleteDirigente,
} from '../../redux/directivaSlice';
import ImageUpload from '../../components/ImageUpload';
import Swal from 'sweetalert2';

const DirectivaAdminPage = () => {
    const dispatch = useDispatch();
    const { dirigentes, loading, error } = useSelector((state) => state.directiva);
    const [showModal, setShowModal] = useState(false);
    const [currentDirigente, setCurrentDirigente] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        cargo: '',
        email: '',
        imagen: null,
    });
    const [formErrors, setFormErrors] = useState({
        nombre: '',
        cargo: '',
        email: '',
        imagen: '',
    });
    const [showFormError, setShowFormError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Formatos de imagen permitidos
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    useEffect(() => {
        dispatch(fetchDirectiva());
    }, [dispatch]);

    const validateField = (name, value) => {
        switch (name) {
            case 'nombre':
                if (!value.trim()) return 'El nombre no puede estar vacío';
                if (value.length < 3) return 'El nombre debe tener al menos 3 caracteres';
                if (value.length > 255) return 'El nombre no puede tener más de 255 caracteres';
                return '';
            case 'cargo':
                if (!value.trim()) return 'El cargo no puede estar vacío';
                if (value.length < 3) return 'El cargo debe tener al menos 3 caracteres';
                if (value.length > 255) return 'El cargo no puede tener más de 255 caracteres';
                return '';
            case 'email':
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'El email debe tener un formato válido';
                return '';
            case 'imagen':
                if (!currentDirigente && !value) return 'La imagen es obligatoria';
                if (value && !ALLOWED_IMAGE_TYPES.includes(value.type)) {
                    return 'Formato de imagen no válido. Use JPG, PNG, WEBP o GIF';
                }
                if (value && value.size > MAX_FILE_SIZE) {
                    return 'La imagen no puede ser mayor a 5MB';
                }
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setFormErrors({ ...formErrors, [name]: validateField(name, value) });
    };

    const handleFileChange = (file) => {
        const error = validateField('imagen', file);
        if (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error en la imagen',
                text: error,
            });
            setFormData({ ...formData, imagen: null });
            setFormErrors({ ...formErrors, imagen: error });
        } else {
            setFormData({ ...formData, imagen: file });
            setFormErrors({ ...formErrors, imagen: '' });
        }
    };

    const isFormValid = () => {
        return (
            formData.nombre.trim() &&
            formData.cargo.trim() &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
            (currentDirigente ? true : formData.imagen) && // La imagen solo es obligatoria para nuevos registros
            !Object.values(formErrors).some(error => error)
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid()) {
            setShowFormError(true);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Por favor complete todos los campos correctamente',
            });
            return;
        }

        setIsSubmitting(true);

        const form = new FormData();
        form.append('nombre', formData.nombre);
        form.append('cargo', formData.cargo);
        form.append('email', formData.email);
        if (formData.imagen) {
            form.append('imagen', formData.imagen);
        }

        try {
             if (currentDirigente) {
        await dispatch(updateDirigente({ id: currentDirigente.id, dirigenteData: form }));
        Swal.fire('Éxito', 'Dirigente actualizado correctamente', 'success');
    } else {
        const resultAction = await dispatch(createDirigente(form));
        if (createDirigente.rejected.match(resultAction)) {
            // Error manejado por Redux Toolkit
            const errorMessage = resultAction.error.message || 'Error desconocido al crear dirigente';
            console.error('Error detallado:', resultAction.error);
            throw new Error(errorMessage);
        }
        Swal.fire('Éxito', 'Dirigente creado correctamente', 'success');
    }
    setShowModal(false);
    resetForm();
} catch (err) {
    console.error('Error completo:', err);
    Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Error al procesar la solicitud',
        footer: typeof err === 'object' ? JSON.stringify(err, null, 2) : ''
    });
} finally {
    setIsSubmitting(false);
}
    };

    const resetForm = () => {
        setFormData({ nombre: '', cargo: '', email: '', imagen: null });
        setFormErrors({ nombre: '', cargo: '', email: '', imagen: '' });
        setCurrentDirigente(null);
        setShowFormError(false);
    };

    const handleEdit = (dirigente) => {
        setCurrentDirigente(dirigente);
        setFormData({
            nombre: dirigente.nombre,
            cargo: dirigente.cargo,
            email: dirigente.email,
            imagen: null,
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: "¡No podrás revertir esto!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteDirigente(id));
                Swal.fire('Éxito', 'Dirigente eliminado correctamente', 'success');
            } catch (err) {
                Swal.fire('Error', 'Error al eliminar el dirigente', 'error');
            }
        }
    };

    const handleAddDirigente = () => {
        resetForm();
        setShowModal(true);
    };

    return (
        <Container className="mt-4">
            <h1 className="text-center mb-4">Administrar Directiva del Sindicato</h1>

            <Button onClick={handleAddDirigente} className="mb-4">
                Agregar Dirigente
            </Button>

            {error && (
    <Alert variant="danger" className="mb-4">
        <strong>Error:</strong> {typeof error === 'object' ? JSON.stringify(error, null, 2) : error}
        <div className="mt-2">
            <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={() => console.error('Error completo:', error)}
            >
                Ver detalles en consola
            </Button>
        </div>
    </Alert>
)}

            <Row className="gy-4">
                {loading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                        <Col key={index} md={6} lg={4}>
                            <Card className="shadow-sm">
                                <Placeholder as={Card.Img} animation="glow" style={{ height: '200px' }} />
                                <Card.Body>
                                    <Placeholder as={Card.Title} animation="glow">
                                        <Placeholder xs={8} />
                                    </Placeholder>
                                    <Placeholder as={Card.Subtitle} animation="glow">
                                        <Placeholder xs={6} />
                                    </Placeholder>
                                    <Placeholder as={Card.Text} animation="glow">
                                        <Placeholder xs={10} />
                                    </Placeholder>
                                    <Placeholder as={Button} animation="glow" variant="warning" xs={6} />
                                    <Placeholder as={Button} animation="glow" variant="danger" xs={6} className="ms-2" />
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                ) : (
                    dirigentes.map((dirigente) => (
                        <Col key={dirigente.id} md={6} lg={4}>
                            <Card className="shadow-sm h-100">
                                <Card.Img 
                                    variant="top" 
                                    src={dirigente.imagen_url} 
                                    alt={`Foto de ${dirigente.nombre}`}
                                    style={{ height: '200px', objectFit: 'cover' }}
                                />
                                <Card.Body className="d-flex flex-column">
                                    <Card.Title>{dirigente.nombre}</Card.Title>
                                    <Card.Subtitle className="mb-2 text-muted">{dirigente.cargo}</Card.Subtitle>
                                    <Card.Text>
                                        <strong>Email:</strong> {dirigente.email}
                                    </Card.Text>
                                    <ButtonGroup className="mt-auto">
                                        <Button 
                                            variant="warning" 
                                            onClick={() => handleEdit(dirigente)}
                                            size="sm"
                                            className="flex-grow-0"
                                        >
                                            <i className="bi bi-pencil"></i> Editar
                                        </Button>
                                        <Button 
                                            variant="danger" 
                                            onClick={() => handleDelete(dirigente.id)}
                                            size="sm"
                                            className="flex-grow-0 ms-2"
                                        >
                                            <i className="bi bi-trash"></i> Eliminar
                                        </Button>
                                    </ButtonGroup>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))
                )}
            </Row>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{currentDirigente ? 'Editar Dirigente' : 'Agregar Dirigente'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        {showFormError && (
                            <Alert variant="danger" className="mb-3">
                                Por favor, corrige los errores antes de enviar el formulario.
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleInputChange}
                                isInvalid={!!formErrors.nombre}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.nombre}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Cargo *</Form.Label>
                            <Form.Control
                                type="text"
                                name="cargo"
                                value={formData.cargo}
                                onChange={handleInputChange}
                                isInvalid={!!formErrors.cargo}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.cargo}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                isInvalid={!!formErrors.email}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                {formErrors.email}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Imagen {!currentDirigente && '*'}</Form.Label>
                            <ImageUpload 
                                onFileSelect={handleFileChange}
                                accept="image/jpeg, image/png, image/webp, image/gif"
                                maxSize={5} // 5MB
                            />
                            {formErrors.imagen && (
                                <div className="text-danger small mt-2">{formErrors.imagen}</div>
                            )}
                            <div className="text-muted small mt-1">
                                Formatos aceptados: JPG, PNG, WEBP, GIF (max 5MB)
                            </div>
                            {currentDirigente && (
                                <Alert variant="info" className="mt-2 small">
                                    <i className="bi bi-info-circle"></i> Si no selecciona una nueva imagen, 
                                    se mantendrá la imagen actual del dirigente.
                                </Alert>
                            )}
                        </Form.Group>

                        <Button 
                            type="submit" 
                            variant="primary" 
                            disabled={!isFormValid() || isSubmitting}
                            className="w-100"
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {currentDirigente ? 'Actualizando...' : 'Agregando...'}
                                </>
                            ) : (
                                currentDirigente ? 'Actualizar Dirigente' : 'Agregar Dirigente'
                            )}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default DirectivaAdminPage;